import { Card, CardContent, Grid, Stack, Typography, useMediaQuery } from '@mui/material'
import { motion } from 'motion/react'
import { FunctionComponent } from 'react'
import gaiaTheme from '../../../root-components/theme'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import { getGaiaReportBodyTextSx, getGaiaReportSectionShellSx } from '../../public-reports/components/gaia-report-web-page-pdf-styles'
import type { IColorPalatte } from '../shared-services/project-reports.model'
import { useReportContentOverride } from '../../public-reports/shared-services/gaia-report-content'

export const ProjectReportWebPageMethodology: FunctionComponent<{
  colorPalatte: IColorPalatte
  screenLabels: any
}> = ({ colorPalatte, screenLabels }) => {
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'))
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'))
  const pdfExport = usePdfExportMode()

  const cardInitial = pdfExport ? { opacity: 1 } : { opacity: 0 }
  const cardTransition = pdfExport ? { duration: 0 } : undefined
  const orgLabel = screenLabels?.organisation?.singular ?? 'organisation'

  const heading = useReportContentOverride('projectMethodology.heading')
  const intro1 = useReportContentOverride('projectMethodology.intro1')
  const intro2 = useReportContentOverride('projectMethodology.intro2')
  const scope1Title = useReportContentOverride('projectMethodology.scope1Title')
  const scope1Body = useReportContentOverride('projectMethodology.scope1Body')
  const scope2Title = useReportContentOverride('projectMethodology.scope2Title')
  const scope2Body = useReportContentOverride('projectMethodology.scope2Body')
  const scope3Title = useReportContentOverride('projectMethodology.scope3Title')
  const scope3Body = useReportContentOverride('projectMethodology.scope3Body')

  return (
    <Stack sx={{
      ...getGaiaReportSectionShellSx({
        backgroundColor: 'white',
        pdfExport,
        webBottomPadding: isMobileModeSm ? 0 : 6,
        webBorderRadius: '40px 40px 0 0',
        overflow: 'hidden',
      }),
      ...(pdfExport ? {} : {
        mt: -5,
        position: 'relative',
        zIndex: 2,
      }),
    }}>
      <Typography variant='h3' sx={{ color: colorPalatte.main, mb: 2 }}>{heading ?? 'Methodology'}</Typography>
      <Typography sx={getGaiaReportBodyTextSx({ color: 'text.secondary', isMobileModeMd, isMobileModeSm, pdfExport, pdfMarginBottom: 2, webMarginBottom: 2 })}>
        {intro1 ?? 'This report follows the GHG Protocol Corporate Standard, the most widely used international accounting tool for government and business leaders to understand, quantify, and manage greenhouse gas emissions.'}
      </Typography>
      <Typography sx={getGaiaReportBodyTextSx({ color: 'text.secondary', isMobileModeMd, isMobileModeSm, pdfExport, pdfMarginBottom: 2, webMarginBottom: isMobileModeMd ? 2 : 8 })}>
        {intro2 ?? 'All emissions are reported in tonnes of CO₂ equivalent (tCO₂e). Location-based Scope 2 figures use grid-average emission factors; market-based figures use supplier-specific factors where available.'}
      </Typography>
      <div className='chart-block'>
        <Stack display='flex' flexDirection='row' justifyContent='space-between' alignItems='center' gap={4} sx={{ height: '100%', mb: pdfExport ? 0 : (isMobileModeMd ? 50 : 28) }}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <motion.div
                style={{ display: 'flex', height: '100%' }}
                initial={cardInitial}
                viewport={{ once: true }}
                whileInView={{ opacity: 1 }}
                transition={cardTransition || { duration: 2, delay: 0.2 }}>
                <Card sx={{ backgroundColor: 'lightGrey.light', height: '100%', boxShadow: 'none', p: isMobileModeMd ? 0.25 : 0.5 }}>
                  <CardContent>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: colorPalatte.main, fontWeight: 600 }}>Scope 1</Typography>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: 'text.primary', mb: isMobileModeMd ? 0.5 : 2 }}>{scope1Title ?? 'Direct Emissions'}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: isMobileModeMd ? '0.85rem' : '1rem' }}>
                      {scope1Body ?? `Direct greenhouse gas emissions from owned or controlled sources, such as company vehicles and on-site fuel combustion. These emissions are directly generated by your ${orgLabel}'s activities.`}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <motion.div
                style={{ display: 'flex', height: '100%' }}
                initial={cardInitial}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={cardTransition || { duration: 2, delay: 0.8 }}>
                <Card sx={{ backgroundColor: 'lightGrey.light', height: '100%', boxShadow: 'none', p: isMobileModeMd ? 0.25 : 0.5 }}>
                  <CardContent>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: colorPalatte.main, fontWeight: 600 }}>Scope 2</Typography>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: 'text.primary', mb: isMobileModeMd ? 0.5 : 2 }}>{scope2Title ?? 'Indirect Emissions - Owned'}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: isMobileModeMd ? '0.85rem' : '1rem' }}>
                      {scope2Body ?? `Indirect emissions from the generation of purchased electricity, steam, heating, and cooling consumed by your ${orgLabel}. Although these emissions occur off-site, they are a result of your ${orgLabel}'s energy use.`}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <motion.div
                style={{ display: 'flex', height: '100%' }}
                initial={cardInitial}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={cardTransition || { duration: 2, delay: 1.4 }}>
                <Card sx={{ backgroundColor: 'lightGrey.light', height: '100%', boxShadow: 'none', p: isMobileModeMd ? 0.25 : 0.5 }}>
                  <CardContent>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: colorPalatte.main, fontWeight: 600 }}>Scope 3</Typography>
                    <Typography variant={isMobileModeMd ? 'subtitle1' : 'h5'} sx={{ color: 'text.primary', mb: isMobileModeMd ? 0.5 : 2 }}>{scope3Title ?? 'Indirect Emissions - Not Owned'}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: isMobileModeMd ? '0.85rem' : '1rem' }}>
                      {scope3Body ?? `Indirect emissions that occur throughout the lifecycle of your ${orgLabel}'s activities, including upstream activities like purchased goods and services, as well as downstream activities such as product use and disposal.`}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Stack>
      </div>
    </Stack>
  )
}
